import { Auth } from '@aws-amplify/auth'
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserSession
} from 'amazon-cognito-identity-js'
import axios from 'axios'
import qs from 'qs'

import { LutienApi } from './LutienApi'

export type TLoginOTP = {
  phoneEmail: string
  code: string
}

export type TSession = {
  idToken: string
  accessToken: string
  refreshToken?: string
}

export type TToken = {
  access_token: string
  expires_in: number
  id_token: string
  refresh_token: string
  token_type: string
}

export type TEmbeddedSession = {
  email?: string
  idToken: { jwtToken: string }
  refreshToken: string
  accessToken: {
    jwtToken: string
    payload: {
      username: string
    }
  }
}

const loginPassword = async (email: string, password: string) => {
  Auth.configure({
    Auth: {
      authenticationFlowType: 'USER_PASSWORD_AUTH'
    }
  })

  return Auth.signIn(email, password)
}

const loginOTP = async (phone?: string, email?: string) => {
  const response = await LutienApi.loginOTP(email || '', phone || '')
  return response
}

const loginOTPChallenge = async (phoneEmail: string, code: string) => {
  Auth.configure({
    Auth: {
      authenticationFlowType: 'CUSTOM_AUTH'
    }
  })

  const userAuth = await Auth.signIn(phoneEmail)
  return Auth.sendCustomChallengeAnswer(userAuth, code)
}

const loginEmbedded = async (session: TEmbeddedSession) => {
  Auth.configure({
    Auth: {
      region: import.meta.env.VITE_AWS_REGION,
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      userPoolWebClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
      authenticationFlowType: 'CUSTOM_AUTH'
    }
  })

  const localSession = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: session?.idToken?.jwtToken || '' }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: session?.refreshToken || '' }),
    AccessToken: new CognitoAccessToken({ AccessToken: session?.accessToken?.jwtToken || '' })
  })

  const localUser = new CognitoUser({
    Username: session.accessToken.payload.username,
    // @ts-ignore
    Pool: Auth.userPool,
    // @ts-ignore
    Storage: Auth.userPool.storage
  })
  localUser.setSignInUserSession(localSession)

  // @ts-ignore
  Auth.currentCredentials = async () => localSession

  return Auth.currentSession()
}

const loginFederated = async (code: string) => {
  Auth.configure({
    Auth: {
      region: import.meta.env.VITE_AWS_REGION,
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      userPoolWebClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
      authenticationFlowType: 'CUSTOM_AUTH'
    }
  })

  const session: any = await getSessionByCode(code)

  const localSession = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: session?.idToken || '' }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: session?.refreshToken || '' }),
    AccessToken: new CognitoAccessToken({ AccessToken: session?.accessToken || '' })
  })

  const accessToken = JSON.parse(atob(session?.accessToken?.split('.')[1] || ''))

  const localUser = new CognitoUser({
    Username: accessToken?.username || '',
    // @ts-ignore
    Pool: Auth.userPool,
    // @ts-ignore
    Storage: Auth.userPool.storage
  })

  localUser.setSignInUserSession(localSession)
  // @ts-ignore
  Auth.currentCredentials = async () => localSession

  return Auth.currentSession()
}

const getSessionByCode = async (code: string): Promise<TSession> => {
  const getRedirectUri = () => {
    const { origin } = window.location
    return `${origin}/auth/federated`
  }

  const dataConfig = qs.stringify({
    grant_type: 'authorization_code',
    code: code,
    redirect_uri: getRedirectUri(),
    client_id: import.meta.env.VITE_COGNITO_SAML_POOL_CLIENT_ID
  })

  const config = {
    method: 'post',
    url: `${import.meta.env.VITE_COGNITO_URL_DOMAIN}/oauth2/token`,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    data: dataConfig
  }

  try {
    const session: TToken = (await axios(config))?.data
    return {
      accessToken: session?.access_token,
      idToken: session?.id_token,
      refreshToken: session?.refresh_token
    }
  } catch (error) {
    throw error
  }
}

const logout = async () => {
  return Auth.signOut()
}

const getUserInfo = async (token: string) => {
  try {
    // Get user attributes from Cognito
    const user = await Auth.currentAuthenticatedUser()
    const attributes = user.attributes || {}
    
    // Map Cognito attributes to expected user format
    return {
      id: attributes['custom:userId'] || attributes.sub,
      email: attributes.email,
      name: attributes.given_name || attributes.name || '',
      lastName: attributes.family_name || '',
      phoneNumber: attributes.phone_number || '',
      role: attributes['custom:role'] || 'ROLE_CUSTOMER',
      govRegCode: attributes['custom:govRegCode'] || '',
      photoURL: attributes.picture || ''
    }
  } catch (error) {
    throw error
  }
}

const getCurrentSession = async () => {
  try {
    return await Auth.currentSession()
  } catch (error) {
    return null
  }
}

const getCurrentAuthenticatedUser = async () => {
  try {
    return await Auth.currentAuthenticatedUser()
  } catch (error) {
    return null
  }
}

const refreshSession = async () => {
  try {
    const cognitoUser = await Auth.currentAuthenticatedUser()
    const currentSession = await Auth.currentSession()
    const refreshToken = currentSession.getRefreshToken()
    
    return new Promise((resolve, reject) => {
      cognitoUser.refreshSession(refreshToken, (err: any, session: any) => {
        if (err) {
          reject(err)
        } else {
          resolve(session)
        }
      })
    })
  } catch (error) {
    throw error
  }
}

export const AuthService = {
  loginPassword,
  loginOTP,
  loginOTPChallenge,
  loginEmbedded,
  loginFederated,
  logout,
  getUserInfo,
  getCurrentSession,
  getCurrentAuthenticatedUser,
  refreshSession,
  getSessionByCode
}